import { usePageTitle } from "@/hooks/use-page-title";
import { parseDateOnly } from "@/lib/dateUtils";
import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Plus, Edit2, Trash2, DollarSign, Calendar, Filter, X, FileText, AlertCircle } from "lucide-react";
import ResponsiveLayout from "@/components/layout/responsive-layout";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import type { PassThroughFeeWithRelations, ServiceType } from "@shared/schema";

const PT_FEE_COLUMNS = [
  { id: 'description', label: 'Description', defaultWidth: 200, minWidth: 120, maxWidth: 400 },
  { id: 'amount', label: 'Amount', defaultWidth: 100, minWidth: 70, maxWidth: 160 },
  { id: 'services', label: 'Services', defaultWidth: 160, minWidth: 100, maxWidth: 300 },
  { id: 'effectivePeriod', label: 'Effective Period', defaultWidth: 140, minWidth: 100, maxWidth: 220 },
  { id: 'chargeType', label: 'Default Charge Type', defaultWidth: 140, minWidth: 110, maxWidth: 200 },
  { id: 'status', label: 'Status', defaultWidth: 80, minWidth: 60, maxWidth: 120 },
  { id: 'actions', label: 'Actions', defaultWidth: 90, minWidth: 70, maxWidth: 120 },
];

const PT_FEE_STORAGE_KEY = 'passThroughFeeColumnWidths';

function getStoredPtFeeWidths(): Record<string, number> {
  try {
    const stored = localStorage.getItem(PT_FEE_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch { return {}; }
}

function savePtFeeWidths(widths: Record<string, number>) {
  try { localStorage.setItem(PT_FEE_STORAGE_KEY, JSON.stringify(widths)); } catch {}
}

function getStatus(fee: PassThroughFeeWithRelations): { label: string; variant: "default" | "secondary" | "outline" | "destructive" } {
  const today = new Date().toISOString().split('T')[0];
  const effectiveDate = fee.effectiveDate;
  const endDate = fee.endDate;

  if (!fee.isActive) {
    return { label: "Inactive", variant: "secondary" };
  }
  if (effectiveDate > today) {
    return { label: "Scheduled", variant: "outline" };
  }
  if (endDate && endDate < today) {
    return { label: "Expired", variant: "destructive" };
  }
  return { label: "Active", variant: "default" };
}

export function PassThroughFeesPage() {
  usePageTitle("Pass-Through Fees");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedFee, setSelectedFee] = useState<PassThroughFeeWithRelations | null>(null);
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [searchQuery, setSearchQuery] = useState<string>("");

  const [columnWidths, setColumnWidths] = useState<Record<string, number>>(() => {
    const stored = getStoredPtFeeWidths();
    const defaults: Record<string, number> = {};
    PT_FEE_COLUMNS.forEach(col => {
      defaults[col.id] = stored[col.id] || col.defaultWidth;
    });
    return defaults;
  });

  const resizingRef = useRef<{ columnId: string; startX: number; startWidth: number } | null>(null);

  const handleResizeStart = useCallback((columnId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[columnId] || PT_FEE_COLUMNS.find(c => c.id === columnId)?.defaultWidth || 100;
    resizingRef.current = { columnId, startX: e.clientX, startWidth };

    const handleMouseMove = (moveEvent: MouseEvent) => {
      if (!resizingRef.current) return;
      const config = PT_FEE_COLUMNS.find(c => c.id === resizingRef.current!.columnId);
      if (!config) return;
      const diff = moveEvent.clientX - resizingRef.current.startX;
      const newWidth = Math.max(config.minWidth, Math.min(config.maxWidth, resizingRef.current.startWidth + diff));
      setColumnWidths(prev => ({ ...prev, [resizingRef.current!.columnId]: newWidth }));
    };

    const handleMouseUp = () => {
      if (resizingRef.current) {
        setColumnWidths(prev => { savePtFeeWidths(prev); return prev; });
      }
      resizingRef.current = null;
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [columnWidths]);

  const getColWidth = useCallback((colId: string) => {
    return columnWidths[colId] || PT_FEE_COLUMNS.find(c => c.id === colId)?.defaultWidth || 100;
  }, [columnWidths]);
  
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: fees = [], isLoading } = useQuery<PassThroughFeeWithRelations[]>({
    queryKey: ['/api/pass-through-fees'],
    refetchOnMount: "always",
  });

  const { data: serviceTypes = [] } = useQuery<ServiceType[]>({
    queryKey: ['/api/service-types'],
    refetchOnMount: "always",
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => apiRequest('DELETE', `/api/pass-through-fees/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pass-through-fees'] });
      toast({
        title: "Success",
        description: "Pass-through fee deleted successfully",
      });
    },
    onError: () => {
      toast({
        title: "Error",
        description: "Failed to delete pass-through fee",
        variant: "destructive",
      });
    },
  });

  const handleEdit = (fee: PassThroughFeeWithRelations) => {
    setSelectedFee(fee);
    setEditModalOpen(true);
  };

  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this pass-through fee?")) {
      deleteMutation.mutate(id);
    }
  };

  const filteredFees = useMemo(() => {
    return fees.filter((fee) => {
      if (serviceTypeFilter !== "all") {
        const hasServiceType = fee.serviceTypes.some(st => st.id === serviceTypeFilter);
        if (!hasServiceType) return false;
      }

      const status = getStatus(fee);
      if (statusFilter === "active" && status.label !== "Active") return false;
      if (statusFilter === "scheduled" && status.label !== "Scheduled") return false;
      if (statusFilter === "expired" && status.label !== "Expired") return false;
      if (statusFilter === "inactive" && status.label !== "Inactive") return false;

      if (searchQuery) {
        const search = searchQuery.toLowerCase();
        if (!fee.description.toLowerCase().includes(search)) return false;
      }

      return true;
    }).sort((a, b) => {
      const statusA = getStatus(a);
      const statusB = getStatus(b);
      const statusOrder = { Active: 0, Scheduled: 1, Expired: 2, Inactive: 3 };
      const statusCompare = (statusOrder[statusA.label as keyof typeof statusOrder] ?? 4) - (statusOrder[statusB.label as keyof typeof statusOrder] ?? 4);
      if (statusCompare !== 0) return statusCompare;
      return a.description.localeCompare(b.description);
    });
  }, [fees, serviceTypeFilter, statusFilter, searchQuery]);

  const clearFilters = () => {
    setServiceTypeFilter("all");
    setStatusFilter("active");
    setSearchQuery("");
  };

  const hasFilters = serviceTypeFilter !== "all" || statusFilter !== "active" || searchQuery !== "";

  const formatDateRange = (effectiveDate: string, endDate: string | null) => {
    const start = parseDateOnly(effectiveDate).toLocaleDateString();
    if (!endDate) return `${start} - Ongoing`;
    return `${start} - ${parseDateOnly(endDate).toLocaleDateString()}`;
  };

  return (
    <ResponsiveLayout 
      title="Pass-Through Fees" 
      subtitle="Manage government and jurisdiction fees passed through to clients"
      currentPage="pass-through-fees"
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <span className="text-sm text-muted-foreground">
              {filteredFees.length} fee{filteredFees.length !== 1 ? 's' : ''}
            </span>
          </div>
          <Button onClick={() => setCreateModalOpen(true)} size="sm" data-testid="button-add-pass-through-fee">
            <Plus className="h-4 w-4 mr-2" />
            Add Pass-Through Fee
          </Button>
        </div>

        <Card>
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-3">
              <Filter className="h-4 w-4 text-muted-foreground" />
              
              <Input
                placeholder="Search fees..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-48"
                data-testid="input-search-fees"
              />

              <Select value={serviceTypeFilter} onValueChange={setServiceTypeFilter}>
                <SelectTrigger className="w-44" data-testid="select-service-type-filter">
                  <SelectValue placeholder="Service" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Services</SelectItem>
                  {serviceTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-32" data-testid="select-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="scheduled">Scheduled</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>

              {hasFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {isLoading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <div className="animate-pulse space-y-3">
                <div className="h-4 bg-muted rounded w-3/4 mx-auto"></div>
                <div className="h-4 bg-muted rounded w-1/2 mx-auto"></div>
              </div>
            </CardContent>
          </Card>
        ) : filteredFees.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium">No pass-through fees found</h3>
              <p className="text-muted-foreground mb-4">
                {fees.length > 0 
                  ? "Try adjusting your filters to see more results." 
                  : "Create your first pass-through fee to get started."}
              </p>
              {fees.length === 0 && (
                <Button onClick={() => setCreateModalOpen(true)} data-testid="button-create-first-fee">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Pass-Through Fee
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed" style={{ minWidth: PT_FEE_COLUMNS.reduce((sum, c) => sum + getColWidth(c.id), 0) }}>
                <thead>
                  <tr className="border-b bg-muted/50">
                    {PT_FEE_COLUMNS.map((col, idx) => (
                      <th
                        key={col.id}
                        className="relative px-3 py-2 text-left text-xs font-medium text-muted-foreground select-none"
                        style={{ width: getColWidth(col.id) }}
                      >
                        <span className={col.id === 'actions' ? 'flex justify-end' : ''}>{col.label}</span>
                        {idx < PT_FEE_COLUMNS.length - 1 && (
                          <div
                            className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/30 active:bg-primary/50 z-10"
                            onMouseDown={(e) => handleResizeStart(col.id, e)}
                          />
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filteredFees.map((fee) => {
                    const status = getStatus(fee);
                    return (
                      <tr key={fee.id} className="border-b hover:bg-muted/30 transition-colors" data-testid={`row-fee-${fee.id}`}>
                        <td className="px-3 py-3" style={{ width: getColWidth('description') }}>
                          <div className="truncate">
                            <span className="font-medium">{fee.description}</span>
                            {fee.internalNotes && (
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-1">
                                {fee.internalNotes}
                              </p>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3" style={{ width: getColWidth('amount') }}>
                          {fee.amount ? (
                            <span className="font-semibold text-green-600">
                              {parseFloat(fee.amount).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                            </span>
                          ) : (
                            <span className="text-muted-foreground italic">Variable</span>
                          )}
                        </td>
                        <td className="px-3 py-3" style={{ width: getColWidth('services') }}>
                          <div className="flex flex-wrap gap-1">
                            {fee.serviceTypes.slice(0, 2).map((st) => (
                              <Badge key={st.id} variant="outline" className="text-xs">
                                {st.name}
                              </Badge>
                            ))}
                            {fee.serviceTypes.length > 2 && (
                              <Badge variant="secondary" className="text-xs">
                                +{fee.serviceTypes.length - 2} more
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3" style={{ width: getColWidth('effectivePeriod') }}>
                          <div className="flex items-center gap-1 text-sm whitespace-nowrap">
                            <Calendar className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                            {formatDateRange(fee.effectiveDate, fee.endDate)}
                          </div>
                        </td>
                        <td className="px-3 py-3" style={{ width: getColWidth('chargeType') }}>
                          <Badge variant={
                            (fee as any).defaultChargeType === 'paid_with_client_card' ? 'secondary' : 
                            (fee as any).defaultChargeType === 'not_applicable' ? 'outline' : 'default'
                          } className="text-xs whitespace-nowrap">
                            {(fee as any).defaultChargeType === 'include_in_invoice' ? 'Include in invoice' : 
                             (fee as any).defaultChargeType === 'paid_with_client_card' ? 'Paid with client card' : 
                             (fee as any).defaultChargeType === 'not_applicable' ? 'Not applicable' : 'Include in invoice'}
                          </Badge>
                        </td>
                        <td className="px-3 py-3" style={{ width: getColWidth('status') }}>
                          <Badge variant={status.variant}>{status.label}</Badge>
                        </td>
                        <td className="px-3 py-3 text-right" style={{ width: getColWidth('actions') }}>
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEdit(fee)}
                              data-testid={`button-edit-fee-${fee.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDelete(fee.id)}
                              data-testid={`button-delete-fee-${fee.id}`}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      <PassThroughFeeModal
        open={createModalOpen}
        onOpenChange={setCreateModalOpen}
        serviceTypes={serviceTypes}
      />

      {selectedFee && (
        <PassThroughFeeModal
          open={editModalOpen}
          onOpenChange={(open) => {
            setEditModalOpen(open);
            if (!open) setSelectedFee(null);
          }}
          serviceTypes={serviceTypes}
          fee={selectedFee}
        />
      )}
    </ResponsiveLayout>
  );
}

function PassThroughFeeModal({
  open,
  onOpenChange,
  serviceTypes,
  fee,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  serviceTypes: ServiceType[];
  fee?: PassThroughFeeWithRelations;
}) {
  const isEdit = !!fee;
  const [description, setDescription] = useState("");
  const [amount, setAmount] = useState("");
  const [effectiveDate, setEffectiveDate] = useState(new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState("");
  const [stripeProductId, setStripeProductId] = useState("");
  const [internalNotes, setInternalNotes] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [selectedServiceTypes, setSelectedServiceTypes] = useState<string[]>([]);
  const [defaultChargeType, setDefaultChargeType] = useState("include_in_invoice");

  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (open) {
      if (fee) {
        setDescription(fee.description ?? "");
        setAmount(fee.amount ?? "");
        setEffectiveDate(fee.effectiveDate ?? new Date().toISOString().split('T')[0]);
        setEndDate(fee.endDate ?? "");
        setStripeProductId(fee.stripeProductId ?? "");
        setInternalNotes(fee.internalNotes ?? "");
        setIsActive(fee.isActive ?? true);
        setSelectedServiceTypes(fee.serviceTypes.map(st => st.id));
        setDefaultChargeType((fee as any).defaultChargeType ?? "include_in_invoice");
      } else {
        setDescription("");
        setAmount("");
        setEffectiveDate(new Date().toISOString().split('T')[0]);
        setEndDate("");
        setStripeProductId("");
        setInternalNotes("");
        setIsActive(true);
        setSelectedServiceTypes([]);
        setDefaultChargeType("include_in_invoice");
      }
    }
  }, [open, fee]);

  const mutation = useMutation({
    mutationFn: async (data: any) => {
      if (isEdit) {
        return apiRequest('PUT', `/api/pass-through-fees/${fee.id}`, data);
      }
      return apiRequest('POST', '/api/pass-through-fees', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/pass-through-fees'] });
      toast({
        title: "Success",
        description: isEdit ? "Pass-through fee updated successfully" : "Pass-through fee created successfully",
      });
      onOpenChange(false);
      resetForm();
    },
    onError: () => {
      toast({
        title: "Error",
        description: isEdit ? "Failed to update pass-through fee" : "Failed to create pass-through fee",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setDescription("");
    setAmount("");
    setEffectiveDate(new Date().toISOString().split('T')[0]);
    setEndDate("");
    setStripeProductId("");
    setInternalNotes("");
    setIsActive(true);
    setSelectedServiceTypes([]);
    setDefaultChargeType("include_in_invoice");
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!description.trim()) {
      toast({
        title: "Validation Error",
        description: "Description is required",
        variant: "destructive",
      });
      return;
    }

    if (selectedServiceTypes.length === 0) {
      toast({
        title: "Validation Error",
        description: "Please select at least one service",
        variant: "destructive",
      });
      return;
    }

    mutation.mutate({
      description: description.trim(),
      amount: amount ? amount : null,
      effectiveDate,
      endDate: endDate || null,
      stripeProductId: stripeProductId || null,
      internalNotes: internalNotes || null,
      defaultChargeType,
      isActive,
      serviceTypeIds: selectedServiceTypes,
    });
  };

  const toggleServiceType = (id: string) => {
    setSelectedServiceTypes(prev =>
      prev.includes(id) ? prev.filter(st => st !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Pass-Through Fee" : "Add Pass-Through Fee"}
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="description">Description *</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., FMCSA MC Application Fee"
              data-testid="input-fee-description"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="amount">Amount (leave empty for variable fees)</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                className="pl-9"
                data-testid="input-fee-amount"
              />
            </div>
            {!amount && (
              <p className="text-xs text-muted-foreground flex items-center gap-1">
                <AlertCircle className="h-3 w-3" />
                Variable fees will be set per work order
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="effectiveDate">Effective Date *</Label>
              <Input
                id="effectiveDate"
                type="date"
                value={effectiveDate}
                onChange={(e) => setEffectiveDate(e.target.value)}
                data-testid="input-effective-date"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date</Label>
              <Input
                id="endDate"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="input-end-date"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label>Services *</Label>
            <div className="border rounded-md p-3 max-h-40 overflow-y-auto space-y-2">
              {serviceTypes.length === 0 ? (
                <p className="text-sm text-muted-foreground">No services available</p>
              ) : (
                serviceTypes.map((type) => (
                  <div key={type.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`st-${type.id}`}
                      checked={selectedServiceTypes.includes(type.id)}
                      onCheckedChange={() => toggleServiceType(type.id)}
                      data-testid={`checkbox-service-type-${type.id}`}
                    />
                    <label
                      htmlFor={`st-${type.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {type.name}
                    </label>
                  </div>
                ))
              )}
            </div>
            {selectedServiceTypes.length > 0 && (
              <p className="text-xs text-muted-foreground">
                {selectedServiceTypes.length} service{selectedServiceTypes.length !== 1 ? 's' : ''} selected
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="internalNotes">Internal Notes</Label>
            <Textarea
              id="internalNotes"
              value={internalNotes}
              onChange={(e) => setInternalNotes(e.target.value)}
              placeholder="Internal guidance for staff working on work orders..."
              rows={3}
              data-testid="input-internal-notes"
            />
            <p className="text-xs text-muted-foreground">
              These notes are for internal staff only and will not be shown to clients.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="defaultChargeType">Default Charge Type</Label>
            <Select value={defaultChargeType} onValueChange={setDefaultChargeType}>
              <SelectTrigger id="defaultChargeType" data-testid="select-default-charge-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="include_in_invoice">Include in invoice</SelectItem>
                <SelectItem value="paid_with_client_card">Paid with client card</SelectItem>
                <SelectItem value="not_applicable">Not applicable</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Sets the default charge type when this fee is added to a work order invoice.
            </p>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox
              id="isActive"
              checked={isActive}
              onCheckedChange={(checked) => setIsActive(!!checked)}
              data-testid="checkbox-is-active"
            />
            <label
              htmlFor="isActive"
              className="text-sm font-medium leading-none cursor-pointer"
            >
              Active
            </label>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={mutation.isPending} data-testid="button-submit-fee">
              {mutation.isPending ? "Saving..." : isEdit ? "Update" : "Create"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PassThroughFeesPage;
